import { useEffect, useState } from 'react';
import { ArrowLeft, Pencil, Plus, ShieldCheck } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import perfisAdminService, {
  type DadosPerfil, type ModuloDisponivel, type PerfilAdministrativo,
} from '../../services/perfisAdminService';

const inicial: DadosPerfil = { nome: '', descricao: '', acesso_total: false, ativo: true };

export function PainelPerfisAdministrativos() {
  const [perfis, setPerfis] = useState<PerfilAdministrativo[]>([]);
  const [modulosDisponiveis, setModulosDisponiveis] = useState<ModuloDisponivel[]>([]);
  const [form, setForm] = useState<DadosPerfil>(inicial);
  const [modulos, setModulos] = useState<string[]>([]);
  const [editando, setEditando] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    const dados = await perfisAdminService.listar();
    setPerfis(dados.perfis);
    setModulosDisponiveis(dados.modulos_disponiveis);
  };

  useEffect(() => {
    carregar().catch(() => toast.error('Erro ao carregar os perfis administrativos.'));
  }, []);

  const limpar = () => { setEditando(null); setForm(inicial); setModulos([]); };

  const alternarModulo = (codigo: string) =>
    setModulos((atuais) => atuais.includes(codigo) ? atuais.filter((c) => c !== codigo) : [...atuais, codigo]);

  const salvar = async () => {
    try {
      setSalvando(true);
      if (editando) await perfisAdminService.atualizar(editando, form, modulos);
      else await perfisAdminService.criar(form, modulos);
      toast.success(editando ? 'Perfil atualizado.' : 'Perfil cadastrado.');
      limpar();
      await carregar();
    } catch (erro: any) {
      toast.error(erro?.response?.data?.errors?.join(', ') || 'Não foi possível salvar o perfil.');
    } finally {
      setSalvando(false);
    }
  };

  const editar = (perfil: PerfilAdministrativo) => {
    setEditando(perfil.id);
    setForm({
      nome: perfil.nome, descricao: perfil.descricao || '',
      acesso_total: perfil.acesso_total, ativo: perfil.ativo,
    });
    setModulos(perfil.acesso_total ? [] : perfil.modulos);
  };

  // Desativar perfil em uso deixa os usuarios sem modulos: o aviso mostra quantos
  // seriam afetados antes de confirmar.
  const alternarSituacao = async (perfil: PerfilAdministrativo) => {
    if (perfil.ativo && perfil.usuarios_vinculados > 0) {
      const confirma = window.confirm(
        `${perfil.usuarios_vinculados} usuário(s) usam o perfil "${perfil.nome}". Desativar mesmo assim?`
      );
      if (!confirma) return;
    }
    try {
      await perfisAdminService.atualizar(
        perfil.id,
        {
          nome: perfil.nome, descricao: perfil.descricao || '',
          acesso_total: perfil.acesso_total, ativo: !perfil.ativo,
        },
        perfil.modulos
      );
      toast.success(perfil.ativo ? 'Perfil desativado.' : 'Perfil ativado.');
      await carregar();
    } catch (erro: any) {
      toast.error(erro?.response?.data?.errors?.join(', ') || 'Não foi possível alterar a situação.');
    }
  };

  return (
    <>
        <section className="bg-white p-5 rounded shadow mb-6">
          <h2 className="font-semibold mb-4">{editando ? 'Editar perfil' : 'Novo perfil'}</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <input className="border rounded p-2" placeholder="Nome do perfil" aria-label="Nome do perfil"
              value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
            <input className="border rounded p-2" placeholder="Descrição" aria-label="Descrição do perfil"
              value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
          </div>

          <label className="flex items-center gap-2 mt-4 text-sm">
            <input type="checkbox" aria-label="Acesso total"
              checked={form.acesso_total} onChange={(e) => setForm({ ...form, acesso_total: e.target.checked })} />
            Acesso total (todos os módulos, inclusive os criados depois)
          </label>

          {!form.acesso_total && (
            <fieldset className="mt-4">
              <legend className="text-sm font-semibold mb-2">Módulos liberados</legend>
              <div className="grid md:grid-cols-3 gap-2">
                {modulosDisponiveis.map((modulo) => (
                  <label key={modulo.codigo} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" aria-label={modulo.nome}
                      checked={modulos.includes(modulo.codigo)} onChange={() => alternarModulo(modulo.codigo)} />
                    {modulo.nome}
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <div className="flex gap-2 mt-4">
            <button onClick={salvar} disabled={salvando || !form.nome.trim()}
              className="flex items-center gap-2 bg-blue-900 disabled:bg-gray-300 text-white rounded px-4 py-2">
              <Plus size={16} />{editando ? 'Salvar alterações' : 'Adicionar perfil'}
            </button>
            {editando && <button onClick={limpar} className="border rounded px-4">Cancelar</button>}
          </div>
        </section>

        <section className="bg-white rounded shadow">
          <div>
            <table className="w-full max-w-full table-fixed text-sm">
              <thead className="hidden sm:table-header-group">
                <tr className="text-left bg-gray-50">
                  <th className="p-3">Perfil</th>
                  <th>Código</th>
                  <th>Módulos</th>
                  <th>Usuários</th>
                  <th>Situação</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {perfis.map((perfil) => (
                  <tr key={perfil.id} className="grid grid-cols-2 gap-2 border-t p-3 sm:table-row sm:p-0">
                    <td className="break-words sm:p-3"><span className="font-semibold sm:hidden">Perfil: </span>{perfil.nome}</td>
                    <td className="break-all"><span className="font-semibold sm:hidden">Código: </span>{perfil.codigo}</td>
                    <td><span className="font-semibold sm:hidden">Módulos: </span>{perfil.acesso_total ? 'Acesso total' : perfil.modulos.length}</td>
                    <td><span className="font-semibold sm:hidden">Usuários: </span>{perfil.usuarios_vinculados}</td>
                    <td><span className="font-semibold sm:hidden">Situação: </span>{perfil.ativo ? 'Ativo' : 'Inativo'}</td>
                    <td className="flex flex-wrap items-center gap-3 sm:p-3">
                      <button onClick={() => editar(perfil)} className="text-blue-800" aria-label={`Editar ${perfil.nome}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => alternarSituacao(perfil)} className="text-blue-800 underline">
                        {perfil.ativo ? 'Desativar' : 'Ativar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!perfis.length && <p className="p-8 text-center text-gray-500">Nenhum perfil cadastrado.</p>}
          </div>
        </section>
    </>
  );
}

export function RedirecionarPerfisAdministrativos() {
  return <Navigate to="/admin/usuarios?aba=perfis" replace />;
}

export default function PerfisAdministrativos() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/admin/home')} className="flex items-center gap-2 text-blue-900 mb-5">
          <ArrowLeft size={18} /> Voltar ao dashboard
        </button>
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="text-blue-900" />
          <div>
            <h1 className="text-2xl font-bold">Perfis administrativos</h1>
            <p className="text-gray-600">Cadastre perfis e marque os módulos que cada um acessa. Nenhuma permissão fica fixada em código.</p>
          </div>
        </div>
        <PainelPerfisAdministrativos />
      </div>
    </main>
  );
}
